import { isServer } from '@/utils/app/detect'
import nextConfig from 'next.config'

declare global {
  interface Window {
    __NEXT_PUBLIC_RUNTIME_CONFIG__?: Record<string, string>
  }
}

/**
 * Get loaded and combined env config
 *
 * @see https://nextjs.org/docs/app/building-your-application/configuring/environment-variables
 *
 */
export const getAppConfig = () => {
  const config: any = isServer()
    ? { ...process.env }
    : {
        ...Object.entries(Object.entries(process.env).filter(([key]) => key.startsWith('NEXT_PUBLIC_'))),
        ...window.__NEXT_PUBLIC_RUNTIME_CONFIG__,
      }

  return {
    basePath: nextConfig().basePath ?? '',
    version: config?.NEXT_PUBLIC_VERSION,
    canonicalPrefix: config?.NEXT_PUBLIC_CANONICAL_PREFIX,
    core: {
      canonicalPrefix: config?.NEXT_PUBLIC_CORE_CANONICAL_PREFIX || 'www.n-tv.de',
      appPrefix: config?.NEXT_PUBLIC_CORE_APP_PREFIX || 'apps.n-tv.de',
    },
    baseUrl: config?.BASE_URL ?? config?.NEXT_PUBLIC_BASE_URL,
    publicBaseUrl: config?.NEXT_PUBLIC_BASE_URL,
    bugsnag: {
      apiKey: config?.NEXT_PUBLIC_BUGSNAG_API_KEY,
      url: config?.NEXT_PUBLIC_BUGSNAG_URL,
      sessionsUrl: config?.NEXT_PUBLIC_BUGSNAG_SESSIONS_URL,
      stage: config?.NEXT_PUBLIC_BUGSNAG_RELEASE_STAGE,
    },
    social: {
      facebook: 'https://www.facebook.com/ntvNachrichten',
      instagram: 'https://www.instagram.com/ntv_nachrichten/',
      x: 'https://x.com/ntvde',
      mastodon: 'https://mstdn.social/@ntv',
      rss: 'https://www.n-tv.de/incoming/RSS-Feeds-von-n-tv-de-article4868034.html',
    },
    store: {
      google: 'https://play.google.com/store/apps/details?id=de.lineas.lit.ntv.android',
      apple: 'https://itunes.apple.com/de/app/n-tv/id337056601?mt=8',
    },
    consent: {
      pur: {
        cookie: '_ntv_pmode',
      },
      sourcepoint: {
        accountId: 1239,
        managerId: 1279621,
        baseEndpoint: 'https://sourcepoint.n-tv.de',
      },
      vendor: {
        ids: {
          nonsocial: {
            addefend: '5e98e7f1b8e05c48537f6114',
            vgwort: '5ef4bd8facb29b0b89038406',
            kameleoon: '5f48d229b8e05c60a307ad97',
          },
          social: {
            facebook: '5ee91b9593fc094b59242e25',
            instagram: '5e717c8e69966540e4554f05',
            tiktok: '5e7f6927b8e05c4e491e7380',
            x: '5e71760b69966540e4554f01',
            youtube: '5e7ac3fae30e7d1bc1ebf5e8',
            truthsocial: '67b6ecc3d3348ce8f6826a7c',
          },
          socialPurposes: ['63036358bcd13b04fe719498', '63036358bcd13b04fe719146'],
        },
        cookies: {
          facebook: '_ntv_co_fb',
          instagram: '_ntv_co_ig',
          tiktok: '_ntv_co_tik',
          x: '_ntv_co_x',
          youtube: '_ntv_co_yt',
          truthsocial: '_ntv_co_ts',
        },
      },
    },
  }
}

export interface BugsnagConfig {
  apiKey: string
  notifyUrl: string
  sessionsUrl: string
  releaseStage: string
  appVersion: string
}

/**
 * Get bugsnag config loaded from env config.
 *
 * @see useConfig
 *
 * @returns {BugsnagConfig} Bugsnag config
 */
export const getBugsnagConfig = (): BugsnagConfig => {
  const config = getAppConfig()
  return <BugsnagConfig>{
    apiKey: config.bugsnag.apiKey,
    appVersion: config.version || 'unknown',
    notifyUrl: config.bugsnag.url,
    sessionsUrl: config.bugsnag.sessionsUrl,
    releaseStage: config.bugsnag.stage,
  }
}
